import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { TemplateService } from '../../../car-wash-website/services/template.service';
import { SectionAccordionComponent } from '../../../shared/components/section-accordion';
import {
  PANEL_PREDEFINED_COLORS,
  isPredefinedPanelColor,
  normalizePickerHexColor,
} from '../../shared/panel-predefined-colors';
import {
  PanelButtonNavigation,
  buttonNavigationToHashLink,
  linkToButtonNavigation,
} from '../../shared/panel-button-navigation';

export interface NavigationTab {
  id: string;
  label: string;
  link: string;
  active?: boolean;
}

export interface PageInfo {
  name: string;
  pageType: string;
  pageId?: string;
}

export interface HeaderData {
  logo?: {
    image?: string;
    alt?: string;
    text?: string;
  };
  navigation?: NavigationTab[];
  ctaButton?: {
    text?: string;
    link?: string;
    buttonNavigation?: PanelButtonNavigation;
    backgroundColor?: string;
    textColor?: string;
  };
  backgroundColor?: string;
}

/**
 * HeaderPanelComponent provides a configurable header editor used in the website builder. It manages logo/media, navigation items, CTA button, and background settings and exposes changes to the parent.
 * @component
 * @selector app-header-panel
 * @example
 *   <app-header-panel [data]="headerData" [pages]="sitePages" (dataChange)="onHeaderChange($event)" (closePanel)="onClosePanel()"></app-header-panel>
 * @input {HeaderData} data - The header configuration object containing logo (image, alt, text), navigation items, CTA button settings, and background color. Used as the primary model that is edited by this component.
 * @input {PageInfo[]} pages - Optional list of pages used to generate and sync navigation items. When provided, navigation is derived from these pages (inactive pages are filtered out).
 * @output {EventEmitter<HeaderData>} dataChange - Emits the updated HeaderData whenever the user makes changes (logo upload, navigation edits, button or background changes).
 * @output {EventEmitter<void>} closePanel - Emits when the panel close action is triggered by the user.
 *
 * Key behaviors and state:
 * - Maintains activeTab ('content' | 'layout') and expandedSection to control accordion UI.
 * - Manages file upload state (selectedFile, logoPreview, isUploadingLogo, uploadError, showReplaceButton) and validates files (allowed types/extensions and max 5MB).
 * - Enforces page title length limits (MAX_PAGE_TITLE_LENGTH = 20) and reports temporary validation errors per page title.
 *
 * Important lifecycle hooks:
 * - ngOnInit: Initializes navigation and expanded sections, ensures CTA defaults and sets initial logo preview if present.
 * - ngOnChanges: Re-initializes navigation and expanded sections when pages change and updates logo preview when data changes.
 *
 * Key methods:
 * - initializeNavigation(): Builds navigation from pages if provided, or ensures a sensible default navigation when none exists.
 * - initializeExpandedSections(): Sets default expanded section (media).
 * - onClose(): Emits closePanel to notify parent to close the editor.
 * - switchTab(tab): Switches between 'content' and 'layout' tabs.
 * - toggleSection(section) / isSectionExpanded(section): Manage single expanded accordion section.
 * - onFileSelected(event), onDrop(event), onDragOver(event): Handlers for file selection and drag/drop interactions.
 * - handleFileUpload(file): Validates, uploads the file via TemplateService, extracts the uploaded URL robustly, updates logo image, and emits changes or sets uploadError on failure.
 * - createImagePreview(file): Creates a local data-URL preview for immediate user feedback while uploading.
 * - onReplaceLogo(): Triggers the hidden file input to replace the existing logo.
 * - onNavigationChange(index, newValue) / onPageTitleInput(event, index): Update and validate navigation labels, enforce max length and show transient errors.
 * - onButtonChange(), onBackgroundChange(): Trigger emitChange when CTA or background are modified.
 * - emitChange(): Emits the current data via dataChange.
 * - getTabLabel(index), getSectionLabel(section), getNavigationItems(): Utility getters for UI labels and navigation access.
 */
@Component({
  selector: 'app-header-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, SectionAccordionComponent],
  templateUrl: './header-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./header-panel.component.scss'],
})
export class HeaderPanelComponent implements OnInit, OnChanges {
  @Input() data: HeaderData = {
    logo: {
      image: '',
      alt: 'Logo',
      text: 'Logo',
    },
    navigation: [],
    ctaButton: {
      text: 'Book Wash',
      link: '#express-wash-packages',
      buttonNavigation: 'express-washes',
      backgroundColor: '#1B5BFF',
      textColor: '#FFFFFF',
    },
    backgroundColor: '#F8F9FA',
  };

  @Input() pages: PageInfo[] = [];

  @Output() dataChange = new EventEmitter<HeaderData>();
  @Output() closePanel = new EventEmitter<void>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSection: string | null = 'media'; // Single expanded section like hero section

  // File upload state
  selectedFile: File | null = null;
  logoPreview: string | null = null;
  isUploadingLogo = false;
  uploadError: string | null = null;
  showReplaceButton = false;

  // Page title validation
  pageTitleErrors: Record<number, string | null> = {};
  readonly MAX_PAGE_TITLE_LENGTH = 20;

  readonly predefinedColors = [...PANEL_PREDEFINED_COLORS];
  readonly buttonNavigationOptions = [
    { value: 'homepage' as PanelButtonNavigation, label: 'Homepage' },
    { value: 'express-washes' as PanelButtonNavigation, label: 'Express Washes' },
    { value: 'express-detail' as PanelButtonNavigation, label: 'Express Detail' },
    { value: 'fleets' as PanelButtonNavigation, label: 'Fleets' },
    { value: 'coupons' as PanelButtonNavigation, label: 'Coupons' },
  ];
  customColors: string[] = [];
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  constructor(private templateService: TemplateService) {}

  /**
   * Initialize the header panel: sets up navigation, expanded sections, logo preview, and default CTA button.
   * @example
   * gOnInit()
   * void
   * @param {void} none - No arguments.
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    this.initializeNavigation();
    this.initializeExpandedSections();

    // Initialize logo preview if logo image exists
    if (this.data.logo?.image) {
      this.logoPreview = this.data.logo.image;
      this.showReplaceButton = true;
    }

    // Ensure button defaults
    if (!this.data.ctaButton) {
      this.data.ctaButton = {
        text: 'Book Wash',
        link: '#express-wash-packages',
        buttonNavigation: 'express-washes',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }

    this.syncCtaButtonState();
    this.loadCustomColorFromData();
  }

  /**
   * Re-initializes navigation and updates the logo preview when the component inputs change.
   * @example
   * gOnChanges({ pages: [{ /* page objects */
  ngOnChanges(changes: any): void {
    // Re-initialize navigation when pages input changes
    if (changes['pages'] && this.pages && this.pages.length > 0) {
      this.initializeNavigation();
      this.initializeExpandedSections();
    }

    // Update logo preview when data changes
    if (changes['data'] && this.data?.logo?.image) {
      this.logoPreview = this.data.logo.image;
      this.showReplaceButton = true;
    }

    if (changes['data']) {
      this.syncCtaButtonState();
      if (this.data?.backgroundColor) {
        this.loadCustomColorFromData();
      }
    }
  }

  /**
   * Initialize the header navigation from this.pages if available, filtering out invisible pages, or set a default Home navigation when none exists.
   * @example
   * initializeNavigation()
   * // undefined
   * @returns {void} Updates this.data.navigation; no value is returned.
   */
  private initializeNavigation(): void {
    // Always generate navigation from pages if pages are provided
    if (this.pages && this.pages.length > 0) {
      // Filter out inactive pages (visible: false means active: false)
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.data.navigation = activePages.map((page, index) => ({
        id: `page_${page.pageType || index}`,
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: index === 0, // First page is active by default
      }));
      console.log('Navigation initialized from pages:', this.data.navigation);
    }
    // If no pages provided but navigation exists, keep it
    else if (!this.data.navigation || this.data.navigation.length === 0) {
      // Set default only if both pages and navigation are empty
      this.data.navigation = [{ id: 'page_home', label: 'Home', link: '#home', active: true }];
    }
  }

  private initializeExpandedSections(): void {
    // Initialize with media section expanded by default
    this.expandedSection = 'media';
  }

  onClose(): void {
    this.closePanel.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle the expansion state of a header section: if the given section is currently expanded it will be collapsed, otherwise it will be expanded.
   * @example
   * oggleSection('about')
   * undefined
   * @param {string} section - Section identifier to toggle.
   * @returns {void} No return value.
   **/
  toggleSection(section: string): void {
    // Same logic as hero section: if clicking an expanded section, collapse it
    // Otherwise, expand this one
    const wasExpanded = this.expandedSection === section;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSection = null;
    } else {
      // If clicking a collapsed section, expand this one
      this.expandedSection = section;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSection === section;
  }

  onFileSelected(event: any): void {
    const [file] = event.target.files ?? [];
    if (file) {
      this.handleFileUpload(file);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const [firstFile] = files;
      this.handleFileUpload(firstFile);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Validate and upload an image or video file (png, jpg, jpeg, gif, svg, mp4), enforce size limit (5MB), update component state and emit changes.
   * @example
   * handleFileUpload(someFile)
   * undefined
   * @param {{File}} {{file}} - The file object selected by the user to validate and upload.
   * @returns {{void}} No return value; updates component state (logoPreview, data.logo, uploadError, isUploadingLogo, showReplaceButton) and emits changes.
   **/
  private handleFileUpload(file: File): void {
    // Validate file type - allow .png, .jpg, .jpeg, .mp4 (based on Figma)
    const validTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/svg+xml',
      'video/mp4',
    ];
    const validExtensions = ['.png', '.jpg', '.jpeg', '.mp4'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!validTypes.includes(file.type) && !hasValidExtension) {
      this.uploadError = 'Invalid file type. Accepted formats - .png, .jpg, .jpeg, .mp4';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.uploadError = 'Max file size - 5MB';
      return;
    }

    this.uploadError = null;
    this.selectedFile = file;
    this.isUploadingLogo = true;

    // Upload file to server
    this.templateService.uploadWebsiteFile(file).subscribe({
      next: uploadResponse => {
        console.log('Logo uploaded successfully:', uploadResponse);

        // Get logo URL from response
        const logoUrl =
          uploadResponse?.data?.url ||
          uploadResponse?.data?.file ||
          uploadResponse?.data?.file_url ||
          uploadResponse?.url ||
          uploadResponse?.file ||
          uploadResponse?.file_url ||
          (uploadResponse?.data && typeof uploadResponse.data === 'string'
            ? uploadResponse.data
            : null);

        if (logoUrl) {
          console.log('Logo URL extracted:', logoUrl);
          this.logoPreview = logoUrl;
          if (this.data.logo) {
            this.data.logo.image = logoUrl;
          } else {
            this.data.logo = {
              image: logoUrl,
              alt: 'Logo',
            };
          }
          this.showReplaceButton = true;
          this.isUploadingLogo = false;
          this.emitChange();
        } else {
          console.error('Logo URL not found in upload response');
          this.uploadError = 'Failed to upload logo. Please try again.';
          this.isUploadingLogo = false;
        }
      },
      error: error => {
        console.error('Error uploading logo:', error);
        this.uploadError = 'Failed to upload logo. Please try again.';
        this.isUploadingLogo = false;
      },
    });
  }

  private createImagePreview(file: File): void {
    // Create local preview while uploading
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.logoPreview = e.target.result;
      this.showReplaceButton = true;
    };
    reader.readAsDataURL(file);
  }

  onReplaceLogo(): void {
    // Trigger file input click
    const fileInput = document.getElementById('logo-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Handle updating a navigation item's label, enforce maximum title length, set temporary error messages, and emit a change event.
   * @example
   * nNavigationChange(2, 'New Page Title')
   * undefined
   * @param {{number}} {{index}} - Index of the navigation item to update.
   * @param {{string}} {{newValue}} - New label value for the navigation item; will be truncated if it exceeds MAX_PAGE_TITLE_LENGTH.
   * @returns {{void}} No return value; the function updates component state (navigation labels and pageTitleErrors) and calls emitChange().
   **/
  onNavigationChange(index: number, newValue: string): void {
    const navItem = this.data.navigation?.[index];
    if (!navItem) return;

    // Validate page title length
    if (newValue && newValue.length > this.MAX_PAGE_TITLE_LENGTH) {
      // Truncate to max length
      navItem.label = newValue.substring(0, this.MAX_PAGE_TITLE_LENGTH);
      // Show error message
      this.pageTitleErrors[index] =
        `Page title cannot exceed ${this.MAX_PAGE_TITLE_LENGTH} characters`;
      // Clear error after 3 seconds
      setTimeout(() => {
        this.pageTitleErrors[index] = null;
      }, 3000);
    } else {
      // Update label and clear error
      navItem.label = newValue || '';
      this.pageTitleErrors[index] = null;
    }

    this.emitChange();
  }

  /**
   * Enforces the maximum page title length on user input, sets a timed error message if exceeded, and clears the error when valid.
   * @example
   * nPageTitleInput(event, 1)
   * undefined
   * @param {{Event}} {{event}} - The input Event from the page title input element.
   * @param {{number}} {{index}} - The index in the pageTitleErrors array corresponding to the input being edited.
   * @returns {{void}} No return value; updates the component state and may modify the input value.
   **/
  onPageTitleInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const newValue = input.value;

    // Prevent typing beyond max length
    if (newValue.length > this.MAX_PAGE_TITLE_LENGTH) {
      input.value = newValue.substring(0, this.MAX_PAGE_TITLE_LENGTH);
      this.pageTitleErrors[index] =
        `Page title cannot exceed ${this.MAX_PAGE_TITLE_LENGTH} characters`;
      // Clear error after 3 seconds
      setTimeout(() => {
        this.pageTitleErrors[index] = null;
      }, 3000);
      return;
    }

    // Clear error if within limit
    this.pageTitleErrors[index] = null;
  }

  onButtonTextChange(value: string): void {
    if (!this.data.ctaButton) {
      return;
    }
    this.data.ctaButton.text = value;
    this.emitChange();
  }

  onButtonNavigationChange(value: string): void {
    if (!this.data.ctaButton) {
      return;
    }
    const navigation = value as PanelButtonNavigation;
    this.data.ctaButton.buttonNavigation = navigation;
    this.data.ctaButton.link = buttonNavigationToHashLink(navigation);
    this.emitChange();
  }

  private syncCtaButtonState(): void {
    if (!this.data.ctaButton) {
      this.data.ctaButton = {
        text: 'Book Wash',
        link: '#express-wash-packages',
        buttonNavigation: 'express-washes',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
      return;
    }

    const navigation =
      this.data.ctaButton.buttonNavigation ?? linkToButtonNavigation(this.data.ctaButton.link);
    this.data.ctaButton.buttonNavigation = navigation;
    this.data.ctaButton.link = buttonNavigationToHashLink(navigation);

    if (!this.data.ctaButton.backgroundColor) {
      this.data.ctaButton.backgroundColor = '#1B5BFF';
    }
    if (!this.data.ctaButton.textColor) {
      this.data.ctaButton.textColor = '#FFFFFF';
    }
  }

  onBackgroundChange(): void {
    this.emitChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitChange();
  }

  onAddColor(): void {
    this.openColorPicker();
  }

  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  closeColorPicker(): void {
    if (this.currentPickerColor) {
      const hexColor = normalizePickerHexColor(this.currentPickerColor);
      this.onBackgroundColorChange(hexColor);
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  onColorPickerChange(): void {
    if (!this.currentPickerColor) {
      return;
    }
    const hexColor = normalizePickerHexColor(this.currentPickerColor);
    this.currentPickerColor = hexColor;
    this.onBackgroundColorChange(hexColor);
  }

  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }
    const bgColor = this.data.backgroundColor;
    if (!isPredefinedPanelColor(bgColor)) {
      this.customColors = [bgColor];
    } else {
      this.customColors = [];
    }
  }

  private addCustomColor(color: string): void {
    if (!isPredefinedPanelColor(color)) {
      this.customColors = [color];
    }
  }

  private emitChange(): void {
    this.dataChange.emit(this.data);
  }

  getTabLabel(index: number): string {
    if (this.data.navigation && this.data.navigation[index]) {
      return this.data.navigation[index].label;
    }
    return `Page ${index + 1}`;
  }

  /**
   * Return a readable label for a section key, using navigation labels for page_* keys.
   * @example
   * etSectionLabel('media')
   * 'Media'
   * @param {{string}} {{section}} - Section key to resolve label for (e.g. 'media', 'button', or 'page_0').
   * @returns {{string}} Resolved human-readable label for the section; falls back to the key if no label found.
   **/
  getSectionLabel(section: string): string {
    // Check if it's a page section
    if (section.startsWith('page_')) {
      const index = parseInt(section.replace('page_', ''), 10);
      if (!isNaN(index) && this.data.navigation && this.data.navigation[index]) {
        return this.data.navigation[index].label;
      }
    }

    const labels: Record<string, string> = {
      media: 'Media',
      button: 'Button',
      background: 'Background',
    };
    return labels[section] || section;
  }

  getNavigationItems(): NavigationTab[] {
    return this.data.navigation || [];
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
